import React, { useState, useRef } from 'react';
import { saveMetrics } from '../db';
import { apiPost } from '../api';

const FIELD_LABELS = {
  fastingGlucose: 'Fasting Glucose (mmol/L)',
  hba1c: 'HbA1c (%)',
  totalCholesterol: 'Total Cholesterol (mmol/L)',
  hdl: 'HDL Cholesterol (mmol/L)',
  ldl: 'LDL Cholesterol (mmol/L)',
  triglycerides: 'Triglycerides (mmol/L)',
  nonHdl: 'Non-HDL Cholesterol (mmol/L)',
  sodium: 'Sodium (mmol/L)',
  potassium: 'Potassium (mmol/L)',
  creatinine: 'Creatinine (umol/L)',
  egfr: 'eGFR (ml/min)',
  alt: 'ALT (U/L)',
  ast: 'AST (U/L)',
  alkalinePhosphatase: 'Alkaline Phosphatase (U/L)',
  bilirubin: 'Bilirubin (umol/L)',
  albumin: 'Albumin (g/L)',
  tsh: 'TSH (mU/L)',
  t4: 'Free T4 (pmol/L)',
  haemoglobin: 'Haemoglobin (g/L)',
  whiteBloodCells: 'White Blood Cells (x10^9/L)',
  platelets: 'Platelets (x10^9/L)',
  ferritin: 'Ferritin (ug/L)',
  vitaminD: 'Vitamin D (nmol/L)',
  vitaminB12: 'Vitamin B12 (pmol/L)',
  folate: 'Folate (nmol/L)',
  uricAcid: 'Uric Acid (mmol/L)',
  crp: 'CRP (mg/L)'
};

const CORE_FIELDS = ['fastingGlucose', 'hba1c', 'totalCholesterol', 'hdl', 'ldl', 'triglycerides', 'nonHdl'];

export default function PDFUpload({ onSaved }) {
  const [stage, setStage] = useState('upload');
  const [uploading, setUploading] = useState(false);
  const [extracted, setExtracted] = useState({});
  const [editedValues, setEditedValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fileName, setFileName] = useState('');
  const [showAll, setShowAll] = useState(false);
  const fileRef = useRef();

  async function handleFile(file) {
    if (!file) return;
    setFileName(file.name);
    setError('');
    setUploading(true);
    setStage('processing');

    try {
      const base64 = await fileToBase64(file);
      const data = await apiPost('/api/extract-blood-test', { fileData: base64, fileType: file.type });

      if (data.error) throw new Error(data.error);
      if (data.count === 0) throw new Error('No blood test values found in this document.');

      setExtracted(data.extracted);
      setEditedValues({ ...data.extracted });
      setStage('review');
    } catch (e) {
      setError(e.message || 'Could not process the document. Please try again.');
      setStage('upload');
    }
    setUploading(false);
  }

  function fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  async function handleSave() {
    setSaving(true);
    try {
      const coreBlood = {};
      const otherBlood = {};
      Object.entries(editedValues).forEach(([k, v]) => {
        if (v && !isNaN(parseFloat(v))) {
          if (CORE_FIELDS.includes(k)) coreBlood[k] = v;
          else otherBlood[k] = v;
        }
      });

      if (Object.keys(coreBlood).length > 0) {
        await saveMetrics('blood', coreBlood);
      }
      if (Object.keys(otherBlood).length > 0) {
        await saveMetrics('extendedBlood', otherBlood);
      }

      setStage('saved');
      if (onSaved) onSaved(editedValues);
    } catch (e) {
      setError('Error saving results. Please try again.');
    }
    setSaving(false);
  }

  const displayFields = showAll ? Object.keys(editedValues) : Object.keys(editedValues).filter(k => CORE_FIELDS.includes(k));

  if (stage === 'upload') return (
    <div>
      <div style={{ border: '2px dashed var(--color-border-secondary)', borderRadius: '12px', padding: '2.5rem', textAlign: 'center', cursor: 'pointer', transition: 'all .2s' }}
        onClick={() => fileRef.current.click()}
        onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
        onMouseEnter={e => e.currentTarget.style.borderColor = '#00AEEF'}
        onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--color-border-secondary)'}
      >
        <div style={{ fontSize: '2.5rem', marginBottom: '.75rem' }}>📋</div>
        <div style={{ fontSize: '.9rem', fontWeight: 500, color: 'var(--color-text-primary)', marginBottom: '.4rem' }}>Drop your blood test here or click to browse</div>
        <div style={{ fontSize: '.78rem', color: 'var(--color-text-secondary)', marginBottom: '.25rem' }}>Supported formats: PDF, JPG, PNG</div>
        <div style={{ fontSize: '.72rem', color: 'var(--color-text-tertiary)' }}>Works with NHS, private labs, and international blood test formats</div>
        <input ref={fileRef} type="file" accept=".pdf,image/*" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
      </div>
      {error && <div style={{ marginTop: '.75rem', padding: '.7rem 1rem', borderRadius: '8px', fontSize: '.78rem', background: 'var(--color-background-danger)', border: '1px solid var(--color-border-danger)', color: 'var(--color-text-danger)' }}>{error}</div>}
    </div>
  );

  if (stage === 'processing') return (
    <div style={{ textAlign: 'center', padding: '2.5rem 1rem' }}>
      <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>🔍</div>
      <div style={{ fontSize: '.9rem', fontWeight: 500, color: 'var(--color-text-primary)', marginBottom: '.4rem' }}>Analysing your blood test...</div>
      <div style={{ fontSize: '.78rem', color: 'var(--color-text-secondary)', marginBottom: '.25rem' }}>{fileName}</div>
      <div style={{ fontSize: '.72rem', color: 'var(--color-text-tertiary)' }}>Rico is reading your results and extracting the values</div>
    </div>
  );

  if (stage === 'saved') return (
    <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
      <div style={{ fontSize: '2rem', marginBottom: '.75rem' }}>✓</div>
      <div style={{ fontSize: '.9rem', fontWeight: 500, color: '#34d399', marginBottom: '.4rem' }}>Blood test results saved</div>
      <div style={{ fontSize: '.78rem', color: 'var(--color-text-secondary)', marginBottom: '1.25rem' }}>Your dashboard and Rico have been updated with your new results.</div>
      <button onClick={() => { setStage('upload'); setExtracted({}); setEditedValues({}); setFileName(''); }} style={{ padding: '.6rem 1.2rem', borderRadius: '8px', border: 'none', background: '#00AEEF', color: '#fff', fontFamily: 'var(--font-sans)', fontSize: '.82rem', cursor: 'pointer', fontWeight: 500 }}>
        Upload Another
      </button>
    </div>
  );

  if (stage === 'review') return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '.5rem' }}>
        <div>
          <div style={{ fontSize: '.9rem', fontWeight: 500, color: 'var(--color-text-primary)' }}>Review extracted values</div>
          <div style={{ fontSize: '.75rem', color: 'var(--color-text-secondary)' }}>{Object.keys(extracted).length} values found in {fileName}</div>
        </div>
        <div style={{ display: 'flex', gap: '.5rem' }}>
          <button onClick={() => setStage('upload')} style={{ padding: '.4rem .8rem', borderRadius: '6px', border: '0.5px solid var(--color-border-secondary)', background: 'transparent', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-sans)', fontSize: '.75rem', cursor: 'pointer' }}>
            Start over
          </button>
        </div>
      </div>

      <div style={{ padding: '.6rem .85rem', borderRadius: '8px', fontSize: '.75rem', marginBottom: '1rem', background: 'var(--color-background-info)', border: '1px solid var(--color-border-info)', color: 'var(--color-text-info)' }}>
        Check the values below before saving. You can edit any field if something looks incorrect.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.6rem', marginBottom: '.75rem' }}>
        {displayFields.map(key => (
          <div key={key}>
            <label style={{ fontSize: '.7rem', color: 'var(--color-text-secondary)', display: 'block', marginBottom: '.2rem', textTransform: 'uppercase', letterSpacing: '.04em' }}>
              {FIELD_LABELS[key] || key}
            </label>
            <input
              type="number"
              step="0.01"
              value={editedValues[key] || ''}
              onChange={e => setEditedValues(prev => ({ ...prev, [key]: e.target.value }))}
              style={{ width: '100%', fontSize: '.82rem' }}
            />
          </div>
        ))}
      </div>

      {Object.keys(editedValues).length > CORE_FIELDS.length && (
        <button onClick={() => setShowAll(!showAll)} style={{ fontSize: '.75rem', color: '#00AEEF', background: 'none', border: 'none', cursor: 'pointer', marginBottom: '.75rem', padding: 0 }}>
          {showAll ? 'Show fewer fields' : `Show all ${Object.keys(editedValues).length} extracted values`}
        </button>
      )}

      {error && <div style={{ padding: '.6rem', borderRadius: '6px', fontSize: '.75rem', background: 'var(--color-background-danger)', border: '1px solid var(--color-border-danger)', color: 'var(--color-text-danger)', marginBottom: '.75rem' }}>{error}</div>}

      <button onClick={handleSave} disabled={saving} style={{ width: '100%', padding: '.7rem', borderRadius: '8px', border: 'none', background: '#00AEEF', color: '#fff', fontFamily: 'var(--font-sans)', fontSize: '.85rem', cursor: 'pointer', fontWeight: 500, opacity: saving ? 0.7 : 1 }}>
        {saving ? 'Saving...' : 'Save to My Health Profile'}
      </button>
    </div>
  );

  return null;
}
